import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api, { type ApiError } from "../api/http";
import type { Business, OnboardingMode, OnboardingStatus } from "../api/types";
import { businessKeys } from "./useBusiness";

export const onboardingKeys = {
  status: ["onboarding", "status"] as const,
};

export function useOnboardingStatus(enabled = true) {
  return useQuery<OnboardingStatus, ApiError>({
    queryKey: onboardingKeys.status,
    queryFn: () => api.get<OnboardingStatus>("/api/v1/onboarding/status/"),
    enabled,
    retry: (failureCount, error) => {
      if (error.status === 404 || error.status === 403) return false;
      return failureCount < 1;
    },
    staleTime: 30_000,
  });
}

export function useCompleteTour() {
  const qc = useQueryClient();
  return useMutation<{ tour_completed: boolean }, ApiError, void>({
    mutationFn: () => api.post("/api/v1/onboarding/tour-complete/"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: onboardingKeys.status });
      qc.invalidateQueries({ queryKey: ["business", "me"] });
    },
  });
}

export function useClearStarterSamples() {
  const qc = useQueryClient();
  return useMutation<{ deleted: number }, ApiError, void>({
    mutationFn: () => api.post("/api/v1/onboarding/clear-starter/"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: onboardingKeys.status });
      qc.invalidateQueries({ queryKey: ["catalog"] });
      qc.invalidateQueries({ queryKey: ["customers"] });
      qc.invalidateQueries({ queryKey: ["goals"] });
    },
  });
}

export function useSetOnboardingMode() {
  const qc = useQueryClient();
  return useMutation<
    { onboarding_mode: OnboardingMode },
    ApiError,
    { mode: OnboardingMode }
  >({
    mutationFn: (data) => api.post("/api/v1/onboarding/set-mode/", data),
    onSuccess: (data) => {
      // Patch the cached business immediately so consumers keyed on
      // onboarding_mode (GuidedTour, the dashboard fallback modal) react
      // without waiting for the refetch — otherwise the fallback modal can
      // flash while the invalidated query is still in flight.
      qc.setQueryData<Business>(businessKeys.my, (old) =>
        old ? { ...old, onboarding_mode: data.onboarding_mode } : old,
      );
      // Picking "samples" seeds new rows; invalidate everything that could now
      // contain starter data so the dashboard / list pages refetch.
      qc.invalidateQueries({ queryKey: onboardingKeys.status });
      qc.invalidateQueries({ queryKey: businessKeys.my });
      qc.invalidateQueries({ queryKey: ["catalog"] });
      qc.invalidateQueries({ queryKey: ["customers"] });
      qc.invalidateQueries({ queryKey: ["sales"] });
      qc.invalidateQueries({ queryKey: ["goals"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}
